/**
 * Permit-to-Work (PTW) — GAP 2
 *
 * Raise, approve, activate, and close work permits for high-risk activities.
 * Status flow: Requested → Approved → Active → Closed / Expired / Cancelled
 * SoD: requester ≠ approver (enforced server-side).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Plus, X, RefreshCw, ShieldAlert, Flame, ArrowUpToLine, Zap, Shovel,
  Loader2, ChevronRight, Clock, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost, apiPatch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const TYPE_ICON = {
  hot_work: Flame,
  height: ArrowUpToLine,
  confined_space: ShieldAlert,
  excavation: Shovel,
  electrical: Zap,
  lifting: RefreshCw,
};

const STATUS_BADGE = {
  Requested: 'bg-amber-50 text-amber-700 border-amber-200',
  Approved:  'bg-blue-50 text-blue-700 border-blue-200',
  Active:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  Closed:    'bg-slate-100 text-slate-500 border-slate-200',
  Expired:   'bg-rose-50 text-rose-600 border-rose-200',
  Cancelled: 'bg-slate-100 text-slate-400 border-slate-200',
};

export default function PermitToWork() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const [permits, setPermits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [permitTypes, setPermitTypes] = useState([]);
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterProject) params.project_id = filterProject;
      if (filterStatus) params.status = filterStatus;
      const d = await apiGet('/safety/permits', params);
      setPermits(d.items || []);
    } catch { toast.error('Failed to load permits'); }
    finally { setLoading(false); }
  }, [filterProject, filterStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiGet('/projects').then((r) => setProjects(Array.isArray(r) ? r : (r?.projects || []))).catch(() => {});
    apiGet('/safety/permit-types').then((r) => setPermitTypes(r?.permit_types || [])).catch(() => {});
  }, []);

  const action = async (verb, body = {}) => {
    if (!selected) return;
    setActionBusy(true);
    try {
      await apiPatch(`/safety/permits/${selected.id}/${verb}`, body);
      toast.success(`Permit ${verb}d`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    } finally { setActionBusy(false); }
  };

  const isRequester = selected?.requested_by_id === user?.user_id;

  return (
    <div className="space-y-5">
      <PageHeader overline="Safety / HSE" title="Permits to Work"
        subtitle="Raise and manage work permits for hot work, height, confined space, excavation, electrical, and lifting." />

      <div className="flex items-center gap-3 flex-wrap">
        <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[220px]"
          data-testid="ptw-filter-project">
          <option value="">All projects</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
          data-testid="ptw-filter-status">
          <option value="">All statuses</option>
          {['Requested', 'Approved', 'Active', 'Closed', 'Expired', 'Cancelled'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button onClick={() => setCreating(true)}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold hover:bg-[#2c3658]"
          data-testid="ptw-new-btn">
          <Plus size={13} /> New Permit
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin mr-2" />Loading…</div>
      ) : permits.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400 text-sm">
          No permits found. Raise a permit before starting any high-risk activity.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full dense-table text-xs" data-testid="ptw-table">
            <thead><tr>
              <th>Permit No</th><th>Type</th><th>Project</th><th>Description</th>
              <th>Valid From</th><th>Valid To</th><th>Requested By</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {permits.map((p) => {
                const Icon = TYPE_ICON[p.permit_type] || ShieldAlert;
                return (
                  <tr key={p.id} className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelected(p)} data-testid={`ptw-row-${p.permit_no}`}>
                    <td className="font-mono font-bold">{p.permit_no}</td>
                    <td>
                      <span className="inline-flex items-center gap-1">
                        <Icon size={12} className="text-slate-500" />
                        {p.permit_type_label}
                      </span>
                    </td>
                    <td className="truncate max-w-[140px]">{p.project_id}</td>
                    <td className="truncate max-w-[200px]">{p.work_description}</td>
                    <td>{p.valid_from ? p.valid_from.slice(0, 16).replace('T', ' ') : '—'}</td>
                    <td>{p.valid_to   ? p.valid_to.slice(0, 16).replace('T', ' ')   : '—'}</td>
                    <td>{p.requested_by}</td>
                    <td>
                      <span className={`px-1.5 py-0.5 text-[10px] border rounded-sm font-display font-bold uppercase ${STATUS_BADGE[p.status] || ''}`}>
                        {p.status}
                      </span>
                    </td>
                    <td><ChevronRight size={13} className="text-slate-400" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail / action panel */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ptw-detail">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-base">{selected.permit_no}</div>
                <div className={`mt-1 inline-flex px-2 py-0.5 text-[10px] border rounded-sm font-display font-bold uppercase ${STATUS_BADGE[selected.status] || ''}`}>
                  {selected.status}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700 ml-4"><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm">
              <Row label="Type" value={selected.permit_type_label} />
              <Row label="Location" value={selected.location} />
              <Row label="Work Description" value={selected.work_description} />
              <Row label="Valid From" value={selected.valid_from?.slice(0, 16).replace('T', ' ')} />
              <Row label="Valid To"   value={selected.valid_to?.slice(0, 16).replace('T', ' ')} />
              <Row label="Requested By" value={selected.requested_by} />
              {selected.approved_by && <Row label="Approved By" value={selected.approved_by} />}
              {selected.closed_by && <Row label="Closed By" value={`${selected.closed_by} — ${selected.closure_remarks || ''}`} />}

              {/* Precautions */}
              {selected.precautions?.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1">Precautions</div>
                  <ul className="space-y-1">
                    {selected.precautions.map((p, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs">
                        {p.checked
                          ? <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
                          : <AlertTriangle size={13} className="text-amber-500 shrink-0" />}
                        {p.item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Gas test */}
              {selected.gas_test && (
                <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-1">
                  <div className="font-semibold text-slate-700">Gas Test Results</div>
                  {Object.entries(selected.gas_test).map(([k, v]) => (
                    <div key={k}><span className="text-slate-500 uppercase text-[10px]">{k}: </span>{v}</div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex flex-wrap gap-2">
              {selected.status === 'Requested' && !isRequester && (
                <button onClick={() => action('approve')} disabled={actionBusy}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-sm text-xs font-display font-bold disabled:opacity-50"
                  data-testid="ptw-approve-btn">
                  {actionBusy ? <Loader2 size={13} className="animate-spin mx-auto" /> : 'Approve'}
                </button>
              )}
              {selected.status === 'Requested' && isRequester && (
                <div className="flex-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-sm p-2 text-center">
                  You raised this permit — a different user must approve (SoD)
                </div>
              )}
              {selected.status === 'Approved' && (
                <button onClick={() => action('activate')} disabled={actionBusy}
                  className="flex-1 bg-emerald-600 text-white py-2 rounded-sm text-xs font-display font-bold disabled:opacity-50"
                  data-testid="ptw-activate-btn">
                  {actionBusy ? <Loader2 size={13} className="animate-spin mx-auto" /> : 'Activate (Work Starts)'}
                </button>
              )}
              {['Approved', 'Active'].includes(selected.status) && (
                <button onClick={() => action('close', { closure_remarks: 'Work completed' })}
                  disabled={actionBusy}
                  className="flex-1 bg-slate-700 text-white py-2 rounded-sm text-xs font-display font-bold disabled:opacity-50"
                  data-testid="ptw-close-btn">
                  {actionBusy ? <Loader2 size={13} className="animate-spin mx-auto" /> : 'Close Permit'}
                </button>
              )}
              {['Requested', 'Approved'].includes(selected.status) && (
                <button onClick={() => action('cancel', { reason: 'Cancelled' })} disabled={actionBusy}
                  className="px-3 py-2 text-xs border border-rose-300 text-rose-600 rounded-sm font-display font-bold disabled:opacity-50"
                  data-testid="ptw-cancel-btn">
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {creating && (
        <NewPermitDrawer projects={projects} permitTypes={permitTypes}
          onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-3 text-sm">
      <span className="text-slate-500 w-32 shrink-0 text-xs font-semibold uppercase tracking-wider pt-0.5">{label}</span>
      <span className="text-slate-800">{value}</span>
    </div>
  );
}

function NewPermitDrawer({ projects, permitTypes, onClose, onSaved }) {
  const [form, setForm] = useState({
    project_id: '', permit_type: '', location: '', work_description: '',
    valid_from: '', valid_to: '', precautions: [],
    gas_test: null,
  });
  const [selectedType, setSelectedType] = useState(null);
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const selectType = (t) => {
    setSelectedType(t);
    setForm((f) => ({
      ...f,
      permit_type: t.type,
      precautions: (t.default_precautions || []).map((item) => ({ item, checked: false })),
      gas_test: t.type === 'confined_space' ? { o2: '', lel: '', h2s: '', co: '', tested_at: '', tested_by: '' } : null,
    }));
  };

  const togglePrecaution = (i) => setForm((f) => ({
    ...f,
    precautions: f.precautions.map((p, idx) => idx === i ? { ...p, checked: !p.checked } : p),
  }));

  const save = async () => {
    if (!form.project_id || !form.permit_type || !form.work_description || !form.valid_from || !form.valid_to)
      return toast.error('Fill all required fields');
    setSaving(true);
    try {
      await apiPost('/safety/permits', form);
      toast.success('Permit raised');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl my-4" data-testid="ptw-new-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div className="font-display font-bold text-base">Raise Permit-to-Work</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Permit type picker */}
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-2">Permit Type *</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {permitTypes.map((t) => {
                const Icon = TYPE_ICON[t.type] || ShieldAlert;
                return (
                  <button key={t.type} onClick={() => selectType(t)} type="button"
                    className={`flex items-center gap-2 p-2.5 rounded-sm border text-left text-xs font-display font-bold transition ${
                      form.permit_type === t.type ? 'border-[#3b4872] bg-[#3b4872]/5 text-[#3b4872]' : 'border-slate-200 bg-white hover:border-slate-400'
                    }`}
                    data-testid={`ptw-type-${t.type}`}>
                    <Icon size={14} className="shrink-0" />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Project *</label>
            <select value={form.project_id} onChange={upd('project_id')}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
              data-testid="ptw-project">
              <option value="">— Select —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Location</label>
            <input value={form.location} onChange={upd('location')} placeholder="Area / floor / grid ref"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Work Description *</label>
            <textarea value={form.work_description} onChange={upd('work_description')} rows={2}
              placeholder="Describe the work to be done"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872] resize-none"
              data-testid="ptw-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Valid From *</label>
              <input type="datetime-local" value={form.valid_from} onChange={upd('valid_from')}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                data-testid="ptw-valid-from" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Valid To *</label>
              <input type="datetime-local" value={form.valid_to} onChange={upd('valid_to')}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]"
                data-testid="ptw-valid-to" />
            </div>
          </div>

          {/* Precautions */}
          {form.precautions.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-600 mb-2">Precautions Checklist</div>
              <div className="space-y-1.5">
                {form.precautions.map((p, i) => (
                  <label key={i} className="flex items-start gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={p.checked} onChange={() => togglePrecaution(i)}
                      className="mt-0.5" />
                    {p.item}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Gas test (confined space) */}
          {form.gas_test !== null && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
              <div className="text-xs font-semibold text-amber-800">Gas Test Results (required for confined space)</div>
              <div className="grid grid-cols-2 gap-2">
                {['o2', 'lel', 'h2s', 'co'].map((gas) => (
                  <div key={gas}>
                    <label className="block text-[10px] font-semibold text-amber-700 mb-0.5 uppercase">{gas} %</label>
                    <input value={form.gas_test[gas] || ''} onChange={(e) => setForm((f) => ({ ...f, gas_test: { ...f.gas_test, [gas]: e.target.value } }))}
                      placeholder="0.0"
                      className="w-full border border-amber-300 rounded px-2 py-1.5 text-xs focus:outline-none" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-amber-700 mb-0.5">Tested At</label>
                  <input type="datetime-local" value={form.gas_test.tested_at || ''} onChange={(e) => setForm((f) => ({ ...f, gas_test: { ...f.gas_test, tested_at: e.target.value } }))}
                    className="w-full border border-amber-300 rounded px-2 py-1.5 text-xs focus:outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-amber-700 mb-0.5">Tested By</label>
                  <input value={form.gas_test.tested_by || ''} onChange={(e) => setForm((f) => ({ ...f, gas_test: { ...f.gas_test, tested_by: e.target.value } }))}
                    className="w-full border border-amber-300 rounded px-2 py-1.5 text-xs focus:outline-none" />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex gap-2">
          <button onClick={save} disabled={saving}
            className="flex-1 bg-[#3b4872] text-white py-2.5 rounded-sm font-display font-bold text-sm disabled:opacity-50"
            data-testid="ptw-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin mx-auto" /> : 'Raise Permit'}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 border border-slate-300 rounded-sm text-sm text-slate-600">Cancel</button>
        </div>
      </div>
    </div>
  );
}
